import { useMemo, type FC } from "react";
import { type UseFormSetValue, type Control } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import { addToast } from "@heroui/react";
import { type CountryCodeValue } from "@/main";
import regionsJson from "$/open_data/regions.json";
import { RegionPicker } from "./RegionPicker";
import { type AddressInfo } from ".";
import { RHFWrapper } from "@/common/RHFWrapper";
import { FormInput } from "@/common/FormInput";
import { addressNamePrefix, parseGeoResults } from "@/utils/address";
import { NumberPicker } from "@/comps/NumberPicker";
import { fetchGeoDataByPostCode } from "$/utils/fetchGeoData";
import { Icon } from "@/assets/icon/Icon";

export const getRegionsByCountry = (code: string) => {
  const regionsByCountry =
    code in regionsJson
      ? regionsJson[code as keyof typeof regionsJson]
      : regionsJson.unknown;
  let index = 0;
  return regionsByCountry.map((item) => {
    let field = item.extra.field;
    if (field === "address") {
      field = `${addressNamePrefix}${index}`;
      index++;
    }
    return {
      ...item,
      extra: {
        ...item.extra,
        field,
      },
    };
  });
};

export const AdministrativeRegions: FC<{
  countryCode: CountryCodeValue;
  control: Control<AddressInfo>;
  setValue: UseFormSetValue<AddressInfo>;
  clearErrors: (name?: string | string[]) => void;
}> = ({ countryCode, control, setValue, clearErrors }) => {
  const { t, i18n } = useTranslation();

  const current = useMemo(
    () => getRegionsByCountry(countryCode),
    [countryCode],
  );

  const showToast = () => {
    addToast({
      title: t("msg:postcode_error"),
      icon: <Icon type="warning" />,
      color: "warning",
    });
  };

  return (
    <div className="mt-2">
      {current.map((region, index) => {
        // 防止没有配置 field 导致的报错
        if (!region.extra.field) {
          return null;
        }

        const className = twMerge(
          "-mt-px",
          index > 0
            ? "border-t-color-border-2 border-t border-b-color-border-2 border-b rounded-none "
            : "",
          index === 0 ? "rounded-t-xl rounded-b-none" : "",
          index === current.length - 1
            ? "rounded-b-xl rounded-t-none border-b-transparent"
            : "",
        );

        if (
          region.extra.type === "cascader" ||
          region.extra.type === "select"
        ) {
          return (
            <RHFWrapper
              name={region.extra.field}
              key={region.extra.field}
              control={control}
              rules={{ required: t("please_select") }}
            >
              <RegionPicker
                label={
                  region.i18n?.[i18n.language as keyof typeof region.i18n] ||
                  region.name
                }
                residenceCountry={countryCode}
                code={region.extra.code}
                classNames={{
                  wrapper: className,
                }}
                onChange={(val) => {
                  if (countryCode === "jpn") {
                    setValue?.("postcode", val?.[2]?.postcode || "");
                  }
                }}
              />
            </RHFWrapper>
          );
        }

        if (region.extra.type === "number") {
          const isJpnPostCode =
            region.extra.field === "postcode" && countryCode === "jpn";
          return (
            <RHFWrapper
              name={region.extra.field}
              key={region.extra.field}
              control={control}
              rules={
                region.extra.required
                  ? { required: t("please_input") }
                  : undefined
              }
            >
              <NumberPicker
                label={
                  region.i18n?.[i18n.language as keyof typeof region.i18n] ||
                  region.name
                }
                classNames={{
                  wrapper: className,
                }}
                validator={
                  isJpnPostCode
                    ? async (value) => {
                        if (value?.length !== 7) {
                          showToast();
                          throw new Error("Invalid Postcode");
                        }
                        const res = await fetchGeoDataByPostCode(
                          region.extra.code,
                          value,
                        );
                        if (!Array.isArray(res) || !res.length) {
                          showToast();
                          throw new Error("Invalid Postcode");
                        }
                        Object.entries(parseGeoResults(res)).forEach(
                          ([key, val]) => {
                            setValue(key, val);
                          },
                        );
                        clearErrors();
                      }
                    : undefined
                }
                prefix={isJpnPostCode ? "〒" : undefined}
              />
            </RHFWrapper>
          );
        }

        return (
          <RHFWrapper
            name={region.extra.field}
            key={region.extra.field}
            control={control}
            rules={
              region.extra.required
                ? { required: t("please_input") }
                : undefined
            }
          >
            <FormInput
              label={
                region.i18n?.[i18n.language as keyof typeof region.i18n] ||
                region.name
              }
              className={className}
            />
          </RHFWrapper>
        );
      })}
    </div>
  );
};
